import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ApprovalRequestDetailSchema, ApprovalRequestStep } from '../../types/approval.type';
import {
  approvalActionTypeLabel,
  approvalModeColor,
  approvalModeLabel,
  approvalScopeLabel,
  approvalStatusColor,
  approvalStatusLabel,
  approvalStepStatusColor,
  approvalStepStatusLabel,
  approvalSubmittedAt,
  approvalUserDisplay,
  approvalUserInitial,
  entityTypeLabel,
  formatApprovalDate,
  isApprovalSubmitter,
  type ApprovalCurrentUser,
} from '../../utils/approval.helpers';

interface ApprovalDetailDrawerProps {
  open: boolean;
  requestId: string | null;
  organizationId: string | null;
  currentUser: ApprovalCurrentUser;
  canApprove: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onRefresh: () => void;
  loadDetail: (id: string, orgId: string) => Promise<ApprovalRequestDetailSchema>;
  onApprove: (id: string, orgId: string, notes?: string) => Promise<void>;
  onReject: (id: string, orgId: string, reason: string) => Promise<void>;
  onCancel: (id: string, orgId: string) => Promise<void>;
}

function getApprovalNote(detail: ApprovalRequestDetailSchema): string | null {
  const withNotes = detail.steps
    .filter((step) => step.status === 'approved' && step.notes?.trim())
    .sort((a, b) => b.step_order - a.step_order);
  return withNotes[0]?.notes?.trim() ?? null;
}

type TimelineEvent = {
  key: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string | null;
  note?: string | null;
  accent?: string;
};

function buildActivityTimeline(
  detail: ApprovalRequestDetailSchema,
  t: TFunction,
): TimelineEvent[] {
  const events: TimelineEvent[] = [
    {
      key: 'submitted',
      icon: <PersonOutlineIcon fontSize="small" />,
      title: t('approvals.detail.submittedByName', {
        name: approvalUserDisplay(detail.requested_by_username, detail.requested_by),
      }),
      subtitle: formatApprovalDate(approvalSubmittedAt(detail)),
      accent: 'primary.main',
    },
  ];

  if (detail.resolved_at && detail.resolved_by_username) {
    if (detail.status === 'rejected') {
      events.push({
        key: 'rejected',
        icon: <CancelOutlinedIcon fontSize="small" />,
        title: t('approvals.detail.rejectedByName', { name: detail.resolved_by_username }),
        subtitle: formatApprovalDate(detail.resolved_at),
        accent: 'error.main',
      });
    } else if (detail.status === 'cancelled') {
      events.push({
        key: 'cancelled',
        icon: <CancelOutlinedIcon fontSize="small" />,
        title: t('approvals.detail.cancelledByName', { name: detail.resolved_by_username }),
        subtitle: formatApprovalDate(detail.resolved_at),
        accent: 'error.main',
      });
    } else {
      events.push({
        key: 'approved',
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        title: t('approvals.detail.approvedByName', { name: detail.resolved_by_username }),
        subtitle: formatApprovalDate(detail.resolved_at),
        note: getApprovalNote(detail),
        accent: 'success.main',
      });
    }
  }

  if (detail.executed_at && detail.status === 'executed') {
    events.push({
      key: 'executed',
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      title: t('approvals.detail.actionCompleted'),
      subtitle: formatApprovalDate(detail.executed_at),
      accent: 'success.main',
    });
  }

  if (detail.status === 'pending' && detail.mode === 'requires_approval') {
    events.push({
      key: 'pending',
      icon: <HourglassTopOutlinedIcon fontSize="small" />,
      title: t('approvals.detail.awaitingApproval'),
      subtitle:
        detail.current_step_order && detail.steps.length > 1
          ? t('approvals.detail.currentlyOnStep', {
              current: detail.current_step_order,
              total: detail.steps.length,
            })
          : t('approvals.detail.waitingForReviewer'),
    });
  }

  return events;
}

function TimelineItem({
  icon,
  title,
  subtitle,
  note,
  accent,
  isLast,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string | null;
  note?: string | null;
  accent?: string;
  isLast?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="stretch">
      <Stack alignItems="center" sx={{ width: 36, flexShrink: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: accent ?? 'action.hover',
            color: accent ? 'common.white' : 'text.secondary',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        {!isLast && (
          <Box
            sx={{
              flex: 1,
              width: 0,
              borderLeft: '2px dotted',
              borderColor: 'divider',
              minHeight: 28,
              my: 0.75,
            }}
          />
        )}
      </Stack>
      <Box sx={{ minWidth: 0, pt: 0.25, pb: isLast ? 0 : 2.5, flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        )}
        {note?.trim() && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              pl: 1.5,
              borderLeft: 2,
              borderColor: 'divider',
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
            }}
          >
            {note}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function ActivityTimeline({ detail, t }: { detail: ApprovalRequestDetailSchema; t: TFunction }) {
  const events = buildActivityTimeline(detail, t);
  return (
    <Stack spacing={0}>
      {events.map((event, index) => (
        <TimelineItem
          key={event.key}
          icon={event.icon}
          title={event.title}
          subtitle={event.subtitle}
          note={event.note}
          accent={event.accent}
          isLast={index === events.length - 1}
        />
      ))}
    </Stack>
  );
}

function StepCard({
  step,
  totalSteps,
  t,
}: {
  step: ApprovalRequestStep;
  totalSteps: number;
  t: TFunction;
}) {
  const isMultiStep = totalSteps > 1;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: step.status === 'pending' ? 'background.paper' : 'action.hover',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {isMultiStep
              ? t('approvals.detail.step', { current: step.step_order, total: totalSteps })
              : t('approvals.detail.review')}
          </Typography>
          {step.status === 'pending' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('approvals.detail.waitingApprover')}
            </Typography>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                {approvalUserInitial(step.acted_by_username, step.acted_by)}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {approvalUserDisplay(step.acted_by_username, step.acted_by)}
                </Typography>
                {step.acted_at && (
                  <Typography variant="caption" color="text.secondary">
                    {formatApprovalDate(step.acted_at)}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </Box>
        <Chip
          label={approvalStepStatusLabel(step.status)}
          color={approvalStepStatusColor(step.status)}
          size="small"
        />
      </Stack>
    </Paper>
  );
}

export const ApprovalDetailDrawer: React.FC<ApprovalDetailDrawerProps> = ({
  open,
  requestId,
  organizationId,
  currentUser,
  canApprove,
  canSubmit,
  onClose,
  onRefresh,
  loadDetail,
  onApprove,
  onReject,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<ApprovalRequestDetailSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!open || !requestId || !organizationId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadDetail(requestId, organizationId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('approvals.detail.failedLoadDetail'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, requestId, organizationId, loadDetail, t]);

  const isSubmitter = detail ? isApprovalSubmitter(detail.requested_by, currentUser) : false;
  const canActOnPending = detail?.status === 'pending' && detail.mode === 'requires_approval';
  const showApproveReject = canActOnPending && canApprove && !isSubmitter;
  const showCancel = canActOnPending && canSubmit && isSubmitter;

  const handleApprove = async () => {
    if (!requestId || !organizationId) return;
    if (isSubmitter) {
      setError(t('approvals.detail.cannotApproveOwn'));
      return;
    }
    setActionLoading(true);
    try {
      await onApprove(requestId, organizationId, approveNotes.trim() || undefined);
      setApproveOpen(false);
      setApproveNotes('');
      onRefresh();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.detail.failedApprove'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!requestId || !organizationId) return;
    if (isSubmitter) {
      setError(t('approvals.detail.cannotRejectOwn'));
      return;
    }
    if (!rejectReason.trim()) {
      setError(t('approvals.detail.rejectionRequired'));
      return;
    }
    setActionLoading(true);
    try {
      await onReject(requestId, organizationId, rejectReason.trim());
      setRejectOpen(false);
      setRejectReason('');
      onRefresh();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.detail.failedReject'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!requestId || !organizationId || !window.confirm(t('approvals.detail.confirmCancel'))) return;
    setActionLoading(true);
    try {
      await onCancel(requestId, organizationId);
      onRefresh();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.detail.failedCancel'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        {detail && !loading && (
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
              spacing={2}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                  {entityTypeLabel(detail.entity_type)} · {approvalScopeLabel(detail.scope)}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, lineHeight: 1.3 }}>
                  {approvalActionTypeLabel(detail.action_type)}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <Chip
                    label={approvalStatusLabel(detail.status)}
                    color={approvalStatusColor(detail.status)}
                    size="small"
                  />
                  <Chip
                    label={approvalModeLabel(detail.mode)}
                    color={approvalModeColor(detail.mode)}
                    size="small"
                  />
                </Stack>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontWeight: 700 }}>
                  {approvalUserInitial(detail.requested_by_username, detail.requested_by)}
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('approvals.detail.submittedBy')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {approvalUserDisplay(detail.requested_by_username, detail.requested_by)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatApprovalDate(approvalSubmittedAt(detail))}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>
        )}

        <DialogContent sx={{ px: 3, py: 3 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {detail && !loading && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('approvals.detail.justification')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {detail.justification}
                </Typography>
              </Paper>

              {detail.rejection_reason && (
                <Alert severity="error" icon={<CancelOutlinedIcon fontSize="inherit" />}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('approvals.detail.rejectionReason')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {detail.rejection_reason}
                  </Typography>
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  {t('approvals.detail.activity')}
                </Typography>
                <ActivityTimeline detail={detail} t={t} />
              </Box>

              {detail.mode === 'requires_approval' && detail.steps.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    {t('approvals.detail.approvalWorkflow')}
                  </Typography>
                  <Stack spacing={1.5}>
                    {detail.steps.map((step) => (
                      <StepCard key={step.id} step={step} totalSteps={detail.steps.length} t={t} />
                    ))}
                  </Stack>
                </Box>
              )}

            </Stack>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} color="inherit">
            {t('approvals.detail.close')}
          </Button>
          <Box sx={{ flex: 1 }} />
          {showCancel && (
            <Button onClick={handleCancel} disabled={actionLoading} color="inherit">
              {t('approvals.detail.cancelRequest')}
            </Button>
          )}
          {showApproveReject && (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setRejectOpen(true)}
                disabled={actionLoading}
              >
                {t('approvals.detail.reject')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => setApproveOpen(true)}
                disabled={actionLoading}
              >
                {t('approvals.detail.approve')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('approvals.detail.approveTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('approvals.detail.confirmApprovalFor')}{' '}
            <strong>
              {detail ? approvalActionTypeLabel(detail.action_type) : t('approvals.detail.thisRequest')}
            </strong>.
          </Typography>
          <TextField
            label={t('approvals.detail.notesOptional')}
            fullWidth
            multiline
            minRows={3}
            value={approveNotes}
            onChange={(e) => setApproveNotes(e.target.value)}
            placeholder={t('approvals.detail.notesPlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApproveOpen(false)}>{t('common.back')}</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading}>
            {t('approvals.detail.confirmApproval')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('approvals.detail.rejectTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('approvals.detail.rejectHelp')}
          </Typography>
          <TextField
            label={t('approvals.detail.rejectionReason')}
            required
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('approvals.detail.rejectPlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectOpen(false)}>{t('common.back')}</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading}>
            {t('approvals.detail.confirmRejection')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApprovalDetailDrawer;
